import { FC, useCallback, useContext, useMemo, useState } from "react";
import { GridColDef } from "@mui/x-data-grid-pro";
import { Typography, Chip, Stack, OutlinedInput, InputAdornment, Select, MenuItem, Button } from "@mui/material";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import moment from "moment";
import { DbInvoiceExpenseInfos } from "../../../Core/Api/Types/db-invoice-expense-infos";
import DataTable, { CellSize } from "../../../Components/Table/table";
import { CurrencyTypes } from "../../../Core/Api/Types/currency-types";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ExpenseTag } from "../../../Core/Api/Types/expense-tag";
import ModalBillingExpense, {
    ModalBillingExpenseProps,
} from "../../../Components/Modals/ModalBillingExpense/modal-billing-expense";
import { ModalContext } from "../../../Components/Modals";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../Components/Table/table-uuids";

interface BillingExpensesTableProps {
    title: string;
    currency: CurrencyTypes;
    expenses: DbInvoiceExpenseInfos[];
    onRowClick?: (expense: DbInvoiceExpenseInfos) => void;
}

const BillingExpenseTable: FC<BillingExpensesTableProps> = (props) => {
    const { t } = useTranslation();
    const { title, expenses, currency = CurrencyTypes.EUR, onRowClick } = props;
    const [filterText, setFilterText] = useState("");
    const [filterFilter, setFilterFilter] = useState("");
    const [filterTag, setFilterTag] = useState("");

    const modalContext = useContext(ModalContext);
    const { openModal } = { ...modalContext };

    const apiGrid = useGridApiRef();

    const filtered = useMemo(() => {
        return expenses.filter(
            (x) =>
                (filterText === "" || x.note?.includesAllWords(filterText)) &&
                (filterTag === "" || x.tag === filterTag) &&
                (filterFilter === "" ||
                    (filterFilter === "EqualsZero" && x.price === 0) ||
                    (filterFilter === "GreatherThanZero" && x.price > 0))
        );
    }, [expenses, filterText, filterFilter, filterTag]);

    const totalExpenses = useMemo(() => {
        if (filtered.length === 0) return 0;
        return filtered
            .map((x) => x.price)
            .reduce((prev, next) => prev + next)
            .toFixed(2);
    }, [filtered]);

    // Cols def
    const cols: GridColDef[] = [
        {
            field: "resourceDate",
            headerName: t("common:date"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            renderCell: (data: any) => (
                <Typography component="span" fontWeight={500} color="text.primary">
                    {moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
                </Typography>
            ),
        },
        {
            field: "tag",
            headerName: t("common:tag"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            renderCell: (data: any) => <Chip label={data.value} />,
        },
        {
            field: "orderReference",
            headerName: t("table:headerName.externalReference"),
            hideable: true,
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            renderCell: (data: any) => (
                <Typography variant="body2" p={1}>
                    {data?.value}
                </Typography>
            ),
        },
        {
            field: "orderCustomer",
            headerName: t("common:customer"),
            hideable: true,
            flex: CellSize.xlarge,
            minWidth: CellSize.xlarge,
            renderCell: (data: any) => (
                <Typography variant="body2" p={1}>
                    {data?.value}
                </Typography>
            ),
        },
        {
            field: "titleName",
            headerName: t("common:title"),
            hideable: true,
            flex: CellSize.xxlarge,
            minWidth: CellSize.xxlarge,
            renderCell: (data: any) => (
                <Typography variant="body2" p={1}>
                    {data?.value}
                </Typography>
            ),
        },
        {
            field: "orderNote",
            headerName: t("table:headerName.noteCheckout"),
            hideable: true,
            flex: CellSize.xlarge,
            minWidth: CellSize.xlarge,
            renderCell: (data: any) => (
                <Typography variant="body2" p={1}>
                    {data?.value}
                </Typography>
            ),
        },
        {
            field: "note",
            headerName: t("common:note"),
            flex: CellSize.xlarge,
            minWidth: CellSize.xlarge,
            renderCell: (data: any) => (
                <Typography variant="body2" p={1}>
                    {data?.value}
                </Typography>
            ),
        },
        {
            field: "price",
            headerName: t("common:price"),
            flex: CellSize.xsmall,
            minWidth: CellSize.xsmall,
            type: "number",
            renderCell: (data: any) => (
                <Typography component="span" fontWeight={500} color="text.primary">
                    {data.value?.toFixed(4)} {currency}
                </Typography>
            ),
        },
        {
            field: "orderPrice",
            headerName: t("table:headerName.externalPrice"),
            hideable: true,
            flex: CellSize.xxsmall,
            minWidth: CellSize.xxsmall,
            type: "number",
            renderCell: (data: any) =>
                data.value ? (
                    <Typography component="span" fontWeight={500} color="text.primary">
                        {data.value?.toFixed(2)} {currency}
                    </Typography>
                ) : (
                    <></>
                ),
        },
    ];

    const onExpenseClick = useCallback((expense: DbInvoiceExpenseInfos) => {
        if (!openModal) return;

        openModal<ModalBillingExpenseProps>(ModalBillingExpense, {
            expense: expense,
            closeModal: () => { },
        });
    }, []);

    const additionalFilters = (
        <>
            <Select
                displayEmpty
                startAdornment={<MergeTypeIcon />}
                IconComponent={ExpandMoreRoundedIcon}
                value={filterTag}
                inputProps={{ "aria-label": "Without label" }}
                onChange={(e) => setFilterTag(e.target.value)}
            >
                <MenuItem value="">{t("common:tag")}</MenuItem>
                {Object.keys(ExpenseTag).map((tag) => (
                    <MenuItem key={tag} value={tag}>
                        {tag}
                    </MenuItem>
                ))}
            </Select>

            <Select
                displayEmpty
                startAdornment={<FilterListIcon />}
                IconComponent={ExpandMoreRoundedIcon}
                value={filterFilter}
                inputProps={{ "aria-label": "Without label" }}
                onChange={(e) => setFilterFilter(e.target.value)}
            >
                <MenuItem value="">{t("common:filters")}</MenuItem>
                <MenuItem key={"GreatherThanZero"} value={"GreatherThanZero"}>
                    {"> 0"}
                </MenuItem>
                <MenuItem key={"EqualsZero"} value={"EqualsZero"}>
                    {t("menu:item.equalWithCount", { count: 0 })}
                </MenuItem>
            </Select></>
    )

    return (
        <Stack gap={2} maxHeight={"calc(100vh - 132px)"}>
            <Typography variant="subtitle2">
                {title} ({totalExpenses} {currency})
            </Typography>

            <DataTable
                tableKey={TableUuids.BILLING_EXPENSE_TABLE}
                rows={filtered}
                columns={cols}
                additionalFilters={additionalFilters}
                columnHeaderHeight={48}
                rowHeight={56}
                getRowHeight={() => "auto"}
                checkboxSelection={false}
                pageSize={20}
                initialColumnVisibility={{
                    orderCustomer: false,
                    orderReference: false,
                    titleName: false,
                    orderNote: false,
                    orderPrice: false,
                }}
                disableRowSelectionOnClick
                pagination={true}
                onRowClick={(row) =>
                    (onRowClick && onRowClick(filtered.find((x) => x.id === row.id)!)) ||
                    onExpenseClick(filtered.find((x) => x.id === row.id)!)
                }
                apiRef={apiGrid}
            />
        </Stack>
    );
};

export default BillingExpenseTable;
